/**
 * Marketing Drip Sequence Templates
 *
 * Pre-built email drip sequence templates for private lender marketing.
 * Covers borrower nurture, retention, and re-engagement flows as well as
 * investor inquiry, reporting, and offering alerts.
 *
 * @see Issue #638 - Marketing Automation Tools
 */

import type { DripSequenceTemplate } from '../types/marketing.types';

// =============================================================================
// SEQUENCE TEMPLATE IDs
// =============================================================================

export const MARKETING_SEQUENCE_IDS = {
  // Borrower sequences
  NEW_LEAD_NURTURE: 'seq-mkt-new-lead-nurture',
  APPLICATION_ABANDONED: 'seq-mkt-application-abandoned',
  POST_FUNDING_LOYALTY: 'seq-mkt-post-funding-loyalty',
  RE_ENGAGEMENT: 'seq-mkt-re-engagement',
  RATE_ALERT: 'seq-mkt-rate-alert',
  REFERRAL_ASK: 'seq-mkt-referral-ask',
  // Investor sequences
  INVESTOR_INQUIRY: 'seq-mkt-investor-inquiry',
  POST_INVESTMENT_UPDATE: 'seq-mkt-post-investment-update',
  NEW_OFFERING_ALERT: 'seq-mkt-new-offering-alert',
  TAX_DOCUMENT_READY: 'seq-mkt-tax-document-ready',
} as const;

// =============================================================================
// BORROWER SEQUENCES
// =============================================================================

export const borrowerSequences: DripSequenceTemplate[] = [
  // ---------------------------------------------------------------------------
  // 1. New Lead Nurture
  // ---------------------------------------------------------------------------
  {
    id: MARKETING_SEQUENCE_IDS.NEW_LEAD_NURTURE,
    name: 'New Lead Nurture',
    description:
      'Automated 5-step nurture sequence for newly captured leads. Introduces your company, loan programs, and builds trust through social proof before a personal outreach touch.',
    trigger: {
      event: 'lead.created',
      conditions: { source: 'website,referral,ppc' },
    },
    steps: [
      {
        delay: 0,
        delayUnit: 'hours',
        type: 'email',
        template: 'new-lead-welcome',
        subject: "Welcome to {{companyName}} — Let's Get Started",
        body: 'Hi {{borrowerName}}, thanks for reaching out. We specialize in fast, flexible private lending for real estate investors like you. Here is a quick overview of what we offer and how we can help you close your next deal.',
      },
      {
        delay: 3,
        delayUnit: 'days',
        type: 'email',
        template: 'loan-programs-overview',
        subject: '{{companyName}} Loan Programs — Find Your Fit',
        body: 'Hi {{borrowerName}}, whether you need a bridge loan, fix-and-flip financing, or a DSCR rental loan, we have a program designed for your strategy. Here is a breakdown of our most popular options.',
      },
      {
        delay: 7,
        delayUnit: 'days',
        type: 'email',
        template: 'calculator-nudge',
        subject: 'How Much Can You Borrow? Try Our Calculator',
        body: 'Hi {{borrowerName}}, curious about your borrowing power? Use our free loan calculator to get an instant estimate based on property value, loan type, and your experience level.',
      },
      {
        delay: 14,
        delayUnit: 'days',
        type: 'email',
        template: 'success-story',
        subject: 'How {{successStoryName}} Closed in {{successStoryDays}} Days',
        body: 'Hi {{borrowerName}}, see how one of our borrowers secured funding and closed their deal in record time. Real results from real investors — and we can do the same for you.',
      },
      {
        delay: 21,
        delayUnit: 'days',
        type: 'task',
        template: 'personal-outreach-call',
        subject: 'Personal Outreach — Call {{borrowerName}}',
        body: 'Call {{borrowerName}} to introduce yourself, answer questions, and discuss their next deal. Reference their initial inquiry and any engagement with previous emails.',
      },
    ],
    exitConditions: ['application.submitted', 'lead.disqualified', 'unsubscribed'],
  },

  // ---------------------------------------------------------------------------
  // 2. Application Abandoned Recovery
  // ---------------------------------------------------------------------------
  {
    id: MARKETING_SEQUENCE_IDS.APPLICATION_ABANDONED,
    name: 'Application Abandoned Recovery',
    description:
      'Recover borrowers who started but did not complete their loan application. Three-step sequence triggered 4 hours after abandonment with increasing urgency.',
    trigger: {
      event: 'intake.abandoned',
      conditions: { minHoursSinceAbandonment: 4 },
    },
    steps: [
      {
        delay: 4,
        delayUnit: 'hours',
        type: 'email',
        template: 'application-reminder',
        subject: '{{borrowerName}}, Your Application Is Waiting',
        body: 'Hi {{borrowerName}}, it looks like you started an application but did not finish. No worries — your progress is saved. Pick up right where you left off and get one step closer to funding.',
      },
      {
        delay: 3,
        delayUnit: 'days',
        type: 'email',
        template: 'application-benefits-recap',
        subject: 'Why Borrowers Choose {{companyName}}',
        body: 'Hi {{borrowerName}}, here is why thousands of real estate investors trust us: fast closings, competitive rates, and a dedicated loan officer assigned to your deal. Complete your application today and see what you qualify for.',
      },
      {
        delay: 7,
        delayUnit: 'days',
        type: 'email',
        template: 'application-final-chance',
        subject: 'Last Chance — Complete Your {{loanProgram}} Application',
        body: 'Hi {{borrowerName}}, your application for a {{loanProgram}} is about to expire. If you still need funding, complete it now or reach out and we will walk you through it personally.',
      },
    ],
    exitConditions: ['intake.completed', 'unsubscribed'],
  },

  // ---------------------------------------------------------------------------
  // 3. Post-Funding Loyalty
  // ---------------------------------------------------------------------------
  {
    id: MARKETING_SEQUENCE_IDS.POST_FUNDING_LOYALTY,
    name: 'Post-Funding Loyalty',
    description:
      'Strengthen borrower relationships after loan funding. Four-step sequence over 90 days covering congratulations, review request, referral ask, and next-deal offer.',
    trigger: {
      event: 'loan.funded',
      conditions: {},
    },
    steps: [
      {
        delay: 0,
        delayUnit: 'hours',
        type: 'email',
        template: 'post-funding-congrats',
        subject: 'Congratulations on Your Funded Loan, {{borrowerName}}!',
        body: 'Hi {{borrowerName}}, congratulations! Your {{loanProgram}} loan has been funded. We are thrilled to have helped you close this deal. Here is what to expect next and how to reach your servicing team.',
      },
      {
        delay: 30,
        delayUnit: 'days',
        type: 'email',
        template: 'review-request',
        subject: 'How Was Your Experience with {{companyName}}?',
        body: 'Hi {{borrowerName}}, it has been a month since your loan funded. We would love to hear about your experience. A quick review helps other investors find the right lending partner — and helps us keep improving.',
      },
      {
        delay: 60,
        delayUnit: 'days',
        type: 'email',
        template: 'referral-ask-post-funding',
        subject: 'Know Someone Who Needs Funding?',
        body: 'Hi {{borrowerName}}, do you know a fellow investor who could use fast, reliable financing? Refer them to {{companyName}} and we will make sure they get the same great experience you did.',
      },
      {
        delay: 90,
        delayUnit: 'days',
        type: 'email',
        template: 'next-deal-offer',
        subject: "Ready for Your Next Deal? Let's Talk",
        body: 'Hi {{borrowerName}}, it has been 90 days since your last closing. If you are eyeing your next investment property, we have pre-approved terms ready for repeat borrowers like you. Let us know and we will get the ball rolling.',
      },
    ],
    exitConditions: ['unsubscribed'],
  },

  // ---------------------------------------------------------------------------
  // 4. Re-engagement Campaign
  // ---------------------------------------------------------------------------
  {
    id: MARKETING_SEQUENCE_IDS.RE_ENGAGEMENT,
    name: 'Re-engagement Campaign',
    description:
      'Win back leads who have been inactive for 90 or more days. Three-step sequence over 14 days featuring a personal touch, market update, and exclusive offer.',
    trigger: {
      event: 'lead.inactive',
      conditions: { minDaysInactive: 90 },
    },
    steps: [
      {
        delay: 0,
        delayUnit: 'hours',
        type: 'email',
        template: 're-engagement-we-miss-you',
        subject: 'We Miss You, {{borrowerName}} — What Have You Been Working On?',
        body: 'Hi {{borrowerName}}, it has been a while since we connected. Whether you are still looking for a deal or already have one lined up, we are here to help. Reply to this email and let us know what you are working on.',
      },
      {
        delay: 5,
        delayUnit: 'days',
        type: 'email',
        template: 're-engagement-market-update',
        subject: "What's Happening in the Market Right Now",
        body: 'Hi {{borrowerName}}, a lot has changed since we last spoke. Here is a quick snapshot of current rates, market trends, and what savvy investors are doing right now. Opportunities are out there — let us help you seize them.',
      },
      {
        delay: 14,
        delayUnit: 'days',
        type: 'email',
        template: 're-engagement-exclusive-offer',
        subject: 'Exclusive Offer for Returning Borrowers — {{companyName}}',
        body: 'Hi {{borrowerName}}, because we value your past interest, we are offering an exclusive rate discount for returning borrowers. This offer is available for a limited time — reach out today to lock it in.',
      },
    ],
    exitConditions: ['email.opened', 'email.clicked', 'application.submitted', 'unsubscribed'],
  },

  // ---------------------------------------------------------------------------
  // 5. Rate Change Alert
  // ---------------------------------------------------------------------------
  {
    id: MARKETING_SEQUENCE_IDS.RATE_ALERT,
    name: 'Rate Change Alert',
    description:
      'Single immediate notification when product rates change. Alerts borrowers about new rate availability and encourages timely action.',
    trigger: {
      event: 'product.rate_changed',
      conditions: {},
    },
    steps: [
      {
        delay: 0,
        delayUnit: 'hours',
        type: 'email',
        template: 'rate-change-notification',
        subject: 'Rate Update: {{loanProgram}} Now Starting at {{newRate}}%',
        body: 'Hi {{borrowerName}}, we have updated our rates for {{loanProgram}} loans. The new starting rate is {{newRate}}%, effective immediately. If you have a deal in the works, now is a great time to lock in your rate. Contact us today.',
      },
    ],
    exitConditions: ['auto'],
  },

  // ---------------------------------------------------------------------------
  // 6. Referral Request
  // ---------------------------------------------------------------------------
  {
    id: MARKETING_SEQUENCE_IDS.REFERRAL_ASK,
    name: 'Referral Request',
    description:
      'Two-step referral ask sent 30 days after loan funding. Requests referrals and follows up with an incentive reminder for those who have not yet referred.',
    trigger: {
      event: 'loan.funded',
      conditions: { delayDays: 30 },
    },
    steps: [
      {
        delay: 30,
        delayUnit: 'days',
        type: 'email',
        template: 'referral-request',
        subject: '{{borrowerName}}, Earn Rewards by Referring a Fellow Investor',
        body: 'Hi {{borrowerName}}, you know what it is like to work with a lender who delivers. If you know a fellow real estate investor who needs funding, send them our way. We will take great care of them — and thank you for the introduction.',
      },
      {
        delay: 37,
        delayUnit: 'days',
        type: 'email',
        template: 'referral-incentive-reminder',
        subject: 'Reminder: Your {{companyName}} Referral Reward Is Waiting',
        body: 'Hi {{borrowerName}}, just a reminder — when you refer an investor who funds a loan with us, you both receive a reward. It is our way of saying thank you. Share your unique referral link today.',
      },
    ],
    exitConditions: ['referral.submitted', 'unsubscribed'],
  },
];

// =============================================================================
// INVESTOR SEQUENCES
// =============================================================================

export const investorSequences: DripSequenceTemplate[] = [
  // ---------------------------------------------------------------------------
  // 7. Investor Inquiry Nurture
  // ---------------------------------------------------------------------------
  {
    id: MARKETING_SEQUENCE_IDS.INVESTOR_INQUIRY,
    name: 'Investor Inquiry Nurture',
    description:
      'Seven-step nurture sequence for new investor inquiries over 30 days. Builds confidence through track record, risk management, testimonials, and a consultation call-to-action.',
    trigger: {
      event: 'investor.inquiry_created',
      conditions: {},
    },
    steps: [
      {
        delay: 0,
        delayUnit: 'hours',
        type: 'email',
        template: 'investor-welcome',
        subject: 'Welcome to {{companyName}} — Investing in Private Lending',
        body: 'Hi {{investorName}}, thank you for your interest in investing with {{companyName}}. We help accredited investors earn consistent, asset-backed returns through private real estate loans. Here is what to expect as you learn more about our platform.',
      },
      {
        delay: 3,
        delayUnit: 'days',
        type: 'email',
        template: 'investor-track-record',
        subject: 'Our Track Record — {{companyName}} by the Numbers',
        body: 'Hi {{investorName}}, transparency matters. Here is a look at our historical performance: total loans funded, average returns, default rates, and how we protect investor capital. Numbers speak louder than promises.',
      },
      {
        delay: 7,
        delayUnit: 'days',
        type: 'email',
        template: 'investor-current-offerings',
        subject: 'Current Investment Opportunities at {{companyName}}',
        body: 'Hi {{investorName}}, here are our currently available investment offerings. Each is backed by real estate collateral and structured to deliver predictable income. Review the details and let us know which interests you.',
      },
      {
        delay: 12,
        delayUnit: 'days',
        type: 'email',
        template: 'investor-risk-management',
        subject: 'How We Protect Your Investment',
        body: 'Hi {{investorName}}, every investment carries risk — but not all risk is equal. Here is how {{companyName}} mitigates risk through conservative underwriting, first-lien positions, property appraisals, and borrower vetting.',
      },
      {
        delay: 17,
        delayUnit: 'days',
        type: 'email',
        template: 'investor-testimonial',
        subject: 'What Our Investors Are Saying',
        body: 'Hi {{investorName}}, do not just take our word for it. Hear from investors who have been earning with {{companyName}} — their experiences, returns, and why they keep reinvesting.',
      },
      {
        delay: 23,
        delayUnit: 'days',
        type: 'email',
        template: 'investor-portfolio-options',
        subject: 'Build Your Portfolio — Investment Options from {{companyName}}',
        body: 'Hi {{investorName}}, whether you prefer short-term notes, diversified fund participation, or whole loan investments, we have options to match your goals and risk tolerance. Here is a breakdown of each.',
      },
      {
        delay: 30,
        delayUnit: 'days',
        type: 'email',
        template: 'investor-consultation-cta',
        subject: "Let's Talk — Schedule Your Investor Consultation",
        body: 'Hi {{investorName}}, you have had a chance to learn about {{companyName}}, our track record, and our offerings. Ready to take the next step? Schedule a one-on-one consultation with our investor relations team. We will answer all your questions and help you get started.',
      },
    ],
    exitConditions: ['investment.committed', 'unsubscribed'],
  },

  // ---------------------------------------------------------------------------
  // 8. Post-Investment Quarterly Update
  // ---------------------------------------------------------------------------
  {
    id: MARKETING_SEQUENCE_IDS.POST_INVESTMENT_UPDATE,
    name: 'Post-Investment Quarterly Update',
    description:
      'Quarterly portfolio performance report sent to active investors. Single-step recurring sequence triggered each quarter after an investment is made.',
    trigger: {
      event: 'investment.made',
      conditions: { repeatInterval: 'quarterly' },
    },
    steps: [
      {
        delay: 0,
        delayUnit: 'hours',
        type: 'email',
        template: 'quarterly-portfolio-report',
        subject: '{{quarterLabel}} Portfolio Performance Report — {{companyName}}',
        body: 'Hi {{investorName}}, here is your quarterly portfolio performance summary. This report includes your current holdings, earned interest, principal repayments, and overall portfolio health. As always, our investor relations team is available if you have questions.',
      },
    ],
    exitConditions: ['investment.redeemed', 'unsubscribed'],
  },

  // ---------------------------------------------------------------------------
  // 9. New Offering Alert
  // ---------------------------------------------------------------------------
  {
    id: MARKETING_SEQUENCE_IDS.NEW_OFFERING_ALERT,
    name: 'New Offering Alert',
    description:
      'Single immediate notification when a new investment offering is published. Alerts existing and prospective investors about new opportunities.',
    trigger: {
      event: 'offering.published',
      conditions: {},
    },
    steps: [
      {
        delay: 0,
        delayUnit: 'hours',
        type: 'email',
        template: 'new-offering-details',
        subject: 'New Investment Opportunity: {{offeringName}} — {{targetReturn}}% Target Return',
        body: 'Hi {{investorName}}, a new investment opportunity is now available on the {{companyName}} platform. {{offeringName}} offers a target return of {{targetReturn}}% with {{loanTerm}} terms secured by {{collateralType}}. Review the full details and reserve your allocation today.',
      },
    ],
    exitConditions: ['auto'],
  },

  // ---------------------------------------------------------------------------
  // 10. Tax Document Ready
  // ---------------------------------------------------------------------------
  {
    id: MARKETING_SEQUENCE_IDS.TAX_DOCUMENT_READY,
    name: 'Tax Document Ready',
    description:
      'Single immediate notification when annual tax documents are generated. Provides a direct download link for the investor portal.',
    trigger: {
      event: 'document.tax_generated',
      conditions: {},
    },
    steps: [
      {
        delay: 0,
        delayUnit: 'hours',
        type: 'email',
        template: 'tax-document-download',
        subject: 'Your {{taxYear}} Tax Document Is Ready — {{companyName}}',
        body: 'Hi {{investorName}}, your {{documentType}} for the {{taxYear}} tax year is now available for download. Log in to your investor portal to access your document. If you have questions about your tax reporting, please consult your tax advisor or reach out to our team.',
      },
    ],
    exitConditions: ['auto'],
  },
];

// =============================================================================
// COMBINED EXPORTS
// =============================================================================

/**
 * All marketing drip sequence templates (borrower + investor).
 */
export const allSequenceTemplates: DripSequenceTemplate[] = [...borrowerSequences, ...investorSequences];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get a marketing sequence template by its unique ID.
 */
export function getSequenceTemplate(id: string): DripSequenceTemplate | undefined {
  return allSequenceTemplates.find((t) => t.id === id);
}
